// Intelligence tab logic shared by today's pane (AITab, CarryingCostSection)
// and the full unit page (UnitV2/tabs/IntelligenceTabV2), so the two cannot
// drift: thresholds, wording and the numbers behind every card.

import { formatCurrency } from './formatting';

// ── Anomaly (P-UNIT-027) ─────────────────────────────────────────────────

export const hasAnomaly = (unit) => Boolean(unit?.AnomalyVerdict && unit.AnomalySeverity !== 'normal');

/** Context chips under the anomaly verdict, in today's order and wording. */
export const anomalyContext = (unit) => {
    const chips = [];
    if (unit.MMYUnitCount != null && unit.MMYUnitCount >= 2) {
        chips.push(`${unit.MMYUnitCount} ${unit.MAKE} ${unit.MODEL} (${unit.YEAR_MANUF != null ? Math.round(unit.YEAR_MANUF) : '—'}) in stock`);
    }
    if (unit.MMYMedianDaysInStock != null) chips.push(`Peer avg: ${Math.round(unit.MMYMedianDaysInStock)}d in stock`);
    if (unit.MMYMedianAskingPrice != null) chips.push(`Peer avg price: ${formatCurrency(unit.MMYMedianAskingPrice)}`);
    if (unit.CategoryUnitCount != null) chips.push(`${unit.CategoryUnitCount} ${unit.CATEGORY} units in inventory`);
    return chips;
};

// ── Sell-speed risk (P-UNIT-029) ─────────────────────────────────────────

export const riskRecommendation = (tier) => {
    if (tier === 'Red') return 'High risk of a slow sale — review pricing and actively market this unit.';
    if (tier === 'Yellow') return 'Moderate slow-mover risk — monitor pricing and buyer interest.';
    return 'Low risk — on track to sell within a healthy window.';
};

// ── Days-to-sell forecast (P-UNIT-030): the MONTHLY survival curve ──────

/** { labels, soldPct } — Now, +1mo … +12mo, chance sold = 1 − S. Null without a curve. */
export const survivalPoints = (unit) => {
    const curve = Array.isArray(unit?.SurvivalCurve) ? unit.SurvivalCurve : null;
    if (!curve || curve.length === 0) return null;
    const months = Math.min(12, curve.length);
    const soldPct = [0, ...curve.slice(0, months).map((s) => Math.round((1 - s) * 100))];
    const labels = ['Now', ...Array.from({ length: months }, (_, i) => `+${i + 1}mo`)];
    return { labels, soldPct };
};

/** Est. carrying to sale: shown only with a daily cost and not for Pending. */
export const carryingToSale = (unit) => (
    unit.CarryingCostDaily != null && unit.StockStatus !== 'P' && unit.MedianDays != null
        ? unit.MedianDays * unit.CarryingCostDaily
        : null
);

// ── Price intelligence (P-UNIT-032) ──────────────────────────────────────

/** Days until the PredictedSaleDate countdown and its chip. Null without a date. */
export const saleCountdown = (dateValue, now = new Date()) => {
    if (!dateValue) return null;
    const days = Math.ceil((new Date(dateValue) - now) / (1000 * 60 * 60 * 24));
    if (Number.isNaN(days)) return null;
    let text;
    if (days < 0) text = `${Math.abs(days)}d overdue`;
    else if (days === 0) text = 'Today';
    else text = `${days}d away`;
    return { days, text, tone: days < 0 ? 'overdue' : days < 14 ? 'soon' : '' };
};

// ── 12-month trajectory (P-UNIT-033) ─────────────────────────────────────

/**
 * Today (PredictedPrice ‖ MLPrediction) plus the forward months (values ≤ 0
 * dropped, at least 2 needed). The band only when every forward month has
 * both bounds; reported as two numbers at month 12.
 */
export const buildTrajectory = (predictions12m, unit) => {
    if (!predictions12m?.predictions) return null;
    const forward = predictions12m.predictions.filter((p) => p != null && p > 0);
    if (forward.length < 2) return null;
    const todayPrice = unit.PredictedPrice || unit.MLPrediction || null;
    const prices = todayPrice ? [todayPrice, ...forward] : forward;
    const labels = todayPrice
        ? ['Today', ...forward.map((_, i) => `+${i + 1}mo`)]
        : forward.map((_, i) => `+${i + 1}mo`);
    const lo = Array.isArray(predictions12m.low) ? predictions12m.low : [];
    const hi = Array.isArray(predictions12m.high) ? predictions12m.high : [];
    const hasBand = lo.length === forward.length && hi.length === forward.length
        && lo.every((v) => v != null && v > 0) && hi.every((v) => v != null && v > 0);
    const bandLow = hasBand ? (todayPrice ? [todayPrice, ...lo] : lo) : null;
    const bandHigh = hasBand ? (todayPrice ? [todayPrice, ...hi] : hi) : null;
    const last = forward.length - 1;
    const bandLowPct = hasBand ? Math.round(((forward[last] - lo[last]) / forward[last]) * 100) : null;
    const bandHighPct = hasBand ? Math.round(((hi[last] - forward[last]) / forward[last]) * 100) : null;
    const firstPrice = prices[0];
    const lastPrice = prices[prices.length - 1];
    const totalDrop = firstPrice - lastPrice;
    const monthlyAvg = prices.length > 1 ? totalDrop / (prices.length - 1) : 0;
    const dropPct = firstPrice > 0 ? (totalDrop / firstPrice) * 100 : 0;
    return {
        labels, prices, firstPrice, lastPrice, totalDrop, monthlyAvg, dropPct,
        hasBand, bandLow, bandHigh, bandLowPct, bandHighPct,
    };
};

/** Caption over the trajectory chart. */
export const trajectoryCaption = (t) => (t.hasBand
    ? `Predicted resale value · shaded band −${t.bandLowPct}% / +${t.bandHighPct}% at month 12`
    : 'Predicted resale value');

// ── Peers (P-UNIT-035 / 036) ─────────────────────────────────────────────

/** Today's four bands on "% of peer avg": <80, ≤120, ≤150, above. */
export const peerBand = (pct) => (pct < 80 ? 'low' : pct <= 120 ? 'inline' : pct <= 150 ? 'high' : 'veryHigh');
// v1's colours per band (unchanged).
export const PEER_BAND_COLORS = Object.freeze({
    low: '#15803d', inline: '#1d4ed8', high: '#b45309', veryHigh: '#b91c1c',
});
const VERDICTS = {
    listing: {
        low: 'Listed shorter than peers', inline: 'In line with peers',
        high: 'Listed longer than most peers', veryHigh: 'Significantly longer than peers',
    },
    hours: {
        low: 'Lower hours than peers', inline: 'In line with peers',
        high: 'Higher hours than most peers', veryHigh: 'Significantly higher than peers',
    },
};

/** { pct, band, verdict, barWidth } for this unit's value against a peer summary. */
export const peerComparison = (value, summary, kind) => {
    if (value == null || !summary) return null;
    const pct = (value / summary.avg) * 100;
    const band = peerBand(pct);
    return {
        pct, band, verdict: VERDICTS[kind][band], barWidth: Math.min(100, Math.min(200, pct) / 2),
    };
};

// ── Listing insights (P-UNIT-042) ────────────────────────────────────────

/** Advertised vs internal price: today's wording (equal reads "$0 below"). */
export const advertisedVsInternal = (advertised, internal) => {
    if (advertised == null || !internal) return null;
    return advertised > internal
        ? { text: `+${formatCurrency(advertised - internal)} above internal`, tone: 'pos' }
        : { text: `${formatCurrency(advertised - internal)} below internal`, tone: 'neg' };
};

/** Tones on the same cuts as getListingAgeColor / getCompletenessColor. */
export const listingAgeTone = (days) => {
    if (!days) return 'quiet';
    if (days <= 90) return 'pos';
    if (days <= 180) return 'inline';
    if (days <= 365) return 'warn';
    return 'neg';
};
export const completenessTone = (pct) => (pct >= 70 ? 'pos' : pct >= 40 ? 'warn' : 'neg');

// ── Whether the tab has anything to show (P-UNIT-028) ───────────────────

export const hasSellSpeedData = (unit) => Boolean(unit.DaysToSellPrediction || unit.PredictedBucket || unit.RiskTier || unit.MedianDays);
export const hasQuoteInterest = (unit) => (unit.QuoteCount != null && unit.QuoteCount > 0) || unit.DaysSinceLastQuote != null;

export const hasAnyInsight = (unit, { mfData, comps, trajectory }) => {
    const hasPriceIntel = Boolean(unit.PredictedPrice && unit.Best_Sell_Price);
    const hasListingIntel = unit.MFDaysListed != null || mfData != null;
    const hasPeerComps = comps?.listing_duration_summary != null || comps?.hours_summary != null;
    return Boolean(hasSellSpeedData(unit) || hasPriceIntel || hasQuoteInterest(unit)
        || hasListingIntel || hasPeerComps || trajectory != null || hasAnomaly(unit));
};

// ── Carrying cost and margin calculator (P-UNIT-037..041) ───────────────

/**
 * Open quotes with a price: quote-equipment rows whose quote_id is an open
 * deal, selling_price > 0, deduped by quote_id (highest wins), with the
 * deal's buyer and salesperson.
 */
export const activeQuotesFor = (pipeline, quoteEquipment) => {
    if (!pipeline || !quoteEquipment) return [];
    const open = pipeline.open || [];
    const openIds = new Set(open.map((q) => q.quote_id));
    const byId = {};
    (quoteEquipment.quotes || [])
        .filter((q) => q.quote_id && openIds.has(q.quote_id) && q.selling_price != null && q.selling_price > 0)
        .forEach((q) => {
            if (!byId[q.quote_id] || q.selling_price > byId[q.quote_id].selling_price) {
                const match = open.find((p) => p.quote_id === q.quote_id);
                byId[q.quote_id] = { ...q, buyer_name: match?.buyer_name, salesperson: match?.salesperson };
            }
        });
    return Object.values(byId);
};

/** The calculator's figures for a price (null = asking). */
export const marginCalc = (unit, calcPrice, activeQuotes = []) => {
    const price = calcPrice != null ? calcPrice : (unit.Best_Sell_Price || 0);
    const calcGross = price - unit.CarryingCostBasis;
    const calcGrossPct = price > 0 ? (calcGross / price) * 100 : 0;
    const calcRemaining = unit.CarryingCostToDate != null ? calcGross - unit.CarryingCostToDate : calcGross;
    const holdAtSale = unit.MarginAtSale != null ? unit.MarginAtSale : null;
    const higherQuotes = calcPrice != null ? activeQuotes.filter((q) => q.selling_price >= calcPrice) : [];
    return {
        price, calcGross, calcGrossPct, calcRemaining, holdAtSale, higherQuotes,
    };
};

/**
 * The carrying-cost rate as a whole %: the unit's own rate, else the
 * dealership's floor plan APR (costs.floor_plan_apr_pct, 7 by default).
 */
export const carryingRatePct = (unit, fallbackPct = 7) => (unit.CarryingCostRate != null ? (unit.CarryingCostRate * 100).toFixed(0) : fallbackPct);

/**
 * Gross margin colour rule: at or above the dealership's margin floor
 * (margin.floor_pct, 10 by default) is good, otherwise amber.
 */
export const grossMarginGood = (pct, floorPct = 10) => pct >= floorPct;

/**
 * The margin calculator's gross margin: 'neg' for a loss, 'good' at or above
 * the margin floor, 'warn' between (same rule as the gross margin figure).
 */
export const calcMarginTone = (amount, pct, floorPct = 10) => {
    if (amount < 0) return 'neg';
    return grossMarginGood(pct, floorPct) ? 'good' : 'warn';
};
